import React, { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import DataTable from "examples/Tables/DataTable";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Close, AccessTime, DateRange, Event, HomeWork, Approval, Dashboard } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ApiCalls from "services/flick_erp_api_call";
import moment from "moment";
import colors from "assets/theme/base/colors";
import { base_url } from "services";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTheme } from "@emotion/react";

function ApproveWFH(props) {
  const user = JSON.parse(localStorage.getItem("user"));
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [description, setDescription] = useState("");
  const [allWfhRows, setAllWfhRows] = useState([]);
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    props.action?.label === "Approved"
      ? "APPROVED"
      : props.action?.label === "Rejected"
      ? "REJECTED"
      : "PENDING"
  );

  useEffect(() => {
    getWFHApproval();
  }, []);

  async function fetchEmployeeDetails(employeeId) {
    if (employeeDetails[employeeId]) {
      return employeeDetails[employeeId];
    }
    try {
      const response = await ApiCalls.getEmployee(employeeId);
      if (response.success && response.data) {
        const employee = response.data;
        const employeeInfo = {
          id: employee.id,
          name: `${employee.user?.firstname || ""} ${employee.user?.lastname || ""}`.trim(),
          email: employee.user?.email || "",
          empId: employee.emp_id || "",
          img: employee.img || "",
        };
        setEmployeeDetails((prev) => ({ ...prev, [employeeId]: employeeInfo }));
        return employeeInfo;
      }
    } catch (error) {
      console.error(`Error fetching employee ${employeeId}:`, error);
    }
    return null;
  }

  async function fetchAllEmployeeDetails(wfhRequests) {
    const employeeIds = [...new Set(wfhRequests.map((req) => req.employee))];
    await Promise.all(employeeIds.map((id) => fetchEmployeeDetails(id)));
  }

  async function getWFHApproval() {
    try {
      const res = await ApiCalls.getWFHApproval("?role=manager");
      if (res.success && res.code === 200) {
        const sortedData = [...(res.data || [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        await fetchAllEmployeeDetails(sortedData);
        setAllWfhRows(sortedData);
      }
    } catch (error) {
      console.error("Error fetching WFH requests:", error);
    }
  }

  const filteredWfhRows = useMemo(
    () => allWfhRows.filter((item) => item.status === activeTab),
    [activeTab, allWfhRows]
  );

  const handleOpenDrawer = (record) => {
    setSelectedRow(record);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
    setDescription("");
  };

  const handleViewDetails = (record) => {
    setSelectedRow(record);
    setDescription("");
    setApprovalDialog(true);
  };

  async function handleApproveWFH(decision) {
    try {
      let response = null;
      const isHR = user?.employee_id === selectedRow?.hr_approved_by;
      const isManager = user?.employee_id === selectedRow?.team_lead_approved_by;
      const isSamePerson = isHR && isManager;
      const payload = { comment: description };

      if (decision === "APPROVED") {
        if (isSamePerson) {
          if (!selectedRow.team_lead_approved) {
            await ApiCalls.approveWFHTeamLead(selectedRow.id, payload);
          }
          if (!selectedRow.hr_approved) {
            await ApiCalls.approveWFHByHR(selectedRow.id, payload);
          }
        } else if (isHR) {
          response = await ApiCalls.approveWFHByHR(selectedRow.id, payload);
        } else if (isManager) {
          response = await ApiCalls.approveWFHTeamLead(selectedRow.id, payload);
        }
      } else {
        response = await ApiCalls.rejectWFHRequest(selectedRow.id, payload);
      }

      if (!response || response.success) {
        setApprovalDialog(false);
        setDescription("");
        await getWFHApproval();
        if (drawerOpen) {
          handleCloseDrawer();
        }
      }
    } catch (error) {
      console.error("Error processing WFH request:", error);
      setApprovalDialog(false);
    }
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: { bg: "#FB8C00", color: "#FFF", label: "PENDING" },
      APPROVED: { bg: "#4CAF50", color: "#FFF", label: "APPROVED" },
      REJECTED: { bg: "#F44336", color: "#FFF", label: "REJECTED" },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Chip
        label={config.label}
        sx={{
          mt: 1,
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: "bold",
        }}
      />
    );
  };

  const tableData = useMemo(
    () => ({
      columns: [
        {
          Header: "Employee",
          accessor: "employee",
          Cell: ({ row }) => {
            const employee = employeeDetails[row.original.employee];
            return (
              <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => handleOpenDrawer(row.original)}>
                <Avatar src={employee?.img ? `${base_url}${employee.img}` : ""} sx={{ width: 30, height: 30 }}>
                  {employee?.name?.charAt(0)}
                </Avatar>
                <MDTypography variant="h6" marginLeft="4px" fontSize="14px" fontWeight="600px">
                  {employee?.name || `Employee ${row.original.employee}`}
                </MDTypography>
              </div>
            );
          },
        },
        {
          Header: "Apply Date",
          accessor: "created_at",
          Cell: ({ row }) => moment(row.original.created_at).format("YYYY-MM-DD"),
        },
        {
          Header: "WFH Date",
          accessor: "date",
          Cell: ({ row }) => row.original?.shift_detail?.date || row.original?.date,
        },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ row }) => getStatusChip(row.original.status),
        },
        {
          Header: "Team Lead",
          accessor: "team_lead_approved",
          Cell: ({ row }) => (row.original.team_lead_approved ? "Approved" : row.original.status === "PENDING" ? "Pending" : "Rejected"),
        },
        {
          Header: "HR",
          accessor: "hr_approved",
          Cell: ({ row }) => (row.original.hr_approved ? "Approved" : row.original.status === "PENDING" ? "Pending" : "Rejected"),
        },
        {
          Header: "Action",
          accessor: "actions",
          Cell: ({ row }) => (
            <MDButton size="small" variant="outlined" color="info" onClick={() => handleViewDetails(row.original)}>
              Review
            </MDButton>
          ),
        },
      ],
      rows: filteredWfhRows,
    }),
    [filteredWfhRows, employeeDetails]
  );

  const selectedEmployee = selectedRow ? employeeDetails[selectedRow.employee] : null;

  return (
    <DashboardLayout>
			<DashboardNavbar />
      <MDBox>
        <Card>
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 2, padding: 3 }}>
            <MDBox
              sx={{
                width: 48,
                height: 48,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
                color: colors.white.main,
              }}
            >
              <Approval />
            </MDBox>
            <MDBox sx={{ display: "flex", flexDirection: "column" }}>
              <MDTypography variant="h4">WFH Management</MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                Track the work from home requests from your team members.
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox px={3} pb={1}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              centered={!isSmallScreen}
              orientation={isSmallScreen ? "vertical" : "horizontal"}
              sx={{
                width: "100%",
                "& .MuiTab-root": {
                  fontSize: isSmallScreen ? "0.8rem" : "1rem",
                },
              }}
            >
              <Tab label="Pending" value="PENDING" />
              <Tab label="Approved" value="APPROVED" />
              <Tab label="Rejected" value="REJECTED" />
            </Tabs>
          </MDBox>
          <MDBox>
            {filteredWfhRows.length > 0 ? (
              <DataTable table={tableData} entriesPerPage={false} />
            ) : (
              <MDBox p={3}>
                <Card
                  sx={{
                    minHeight: 220,
                    borderRadius: "20px",
                    border: "1px dashed rgba(123, 128, 154, 0.32)",
                    boxShadow: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(180deg, rgba(248, 249, 250, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
                  }}
                >
                  <MDBox textAlign="center" px={3}>
                    <MDTypography variant="h5" fontWeight="medium" color="dark">
                      No WFH requests available in this stage
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                      Switch tabs to review other request statuses.
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>WFH Approval Action</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Add comment here"
            multiline
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={() => handleApproveWFH("APPROVED")}>
            Approve
          </MDButton>
          <MDButton color="error" onClick={() => handleApproveWFH("REJECTED")}>
            Reject
          </MDButton>
          <MDButton onClick={() => setApprovalDialog(false)}>Cancel</MDButton>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer} PaperProps={{ sx: { width: 360, margin: "0 !important", height: "100%", borderRadius: "0" } }}>
        <Box
          sx={{
            p: 2,
            height: "100%",
            overflow: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            "-ms-overflow-style": "none",
            scrollbarWidth: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <MDTypography variant="h6" fontWeight="bold">
              Employee WFH Details
            </MDTypography>
            <MDButton onClick={handleCloseDrawer}>
              <Close />
            </MDButton>
          </Box>

          {selectedRow ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Avatar alt="Profile Photo" sx={{ width: 70, height: 70 }}>
                  <img
                    src={selectedEmployee?.img ? `${base_url}${selectedEmployee.img}` : ""}
                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "50%" }}
                  />
                </Avatar>
                <Box>
                  <MDTypography variant="h6" fontWeight="bold">
                    {selectedEmployee?.name || "Employee"}
                  </MDTypography>
                  {getStatusChip(selectedRow.status)}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Event fontSize="12px" />
                  <Box>
                    <MDTypography variant="body1" fontSize="14px" fontWeight="600px">WFH Date</MDTypography>
                    <MDTypography variant="body2" fontSize="12px">{selectedRow?.shift_detail?.date || selectedRow?.date}</MDTypography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <DateRange fontSize="12px" />
                  <Box>
                    <MDTypography variant="body1" fontSize="14px" fontWeight="600px">Applied On</MDTypography>
                    <MDTypography variant="body2" fontSize="12px">{moment(selectedRow.created_at).format("YYYY-MM-DD")}</MDTypography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <AccessTime fontSize="12px" />
                  <Box>
                    <MDTypography variant="body1" fontSize="14px" fontWeight="600px">Team Lead</MDTypography>
                    <MDTypography variant="body2" fontSize="12px">{selectedRow.team_lead_approved ? "Approved" : selectedRow.status === "PENDING" ? "Pending" : "Rejected"}</MDTypography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <HomeWork fontSize="12px" />
                  <Box>
                    <MDTypography variant="body1" fontSize="14px" fontWeight="600px">HR Status</MDTypography>
                    <MDTypography variant="body2" fontSize="12px">{selectedRow.hr_approved ? "Approved" : selectedRow.status === "PENDING" ? "Pending" : "Rejected"}</MDTypography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ marginTop: "14px" }}>
                <MDTypography variant="h6" fontSize="16px" fontWeight="600px">Reason</MDTypography>
                <MDTypography variant="body1" fontSize="14px" whiteSpace="pre-wrap" fontWeight="400px">
                  {selectedRow.reason}
                </MDTypography>
              </Box>

              {selectedRow?.status?.toUpperCase() === "PENDING" ? (
                <Card sx={{ marginTop: "14px", padding: "14px" }}>
                  <MDTypography variant="h6" fontSize="14px" fontWeight="600px" sx={{ marginBottom: "8px" }}>
                    Update Status
                  </MDTypography>
                  <TextField placeholder="Description" fullWidth onChange={(e) => setDescription(e.target.value)} value={description} />
                  <Box display="flex" justifyContent="flex-end" sx={{ gap: "6px", marginTop: "16px" }}>
                    <MDButton size="small" onClick={() => handleApproveWFH("APPROVED")} variant="contained" color="success" disabled={!description}>
                      Accept
                    </MDButton>
                    <MDButton size="small" onClick={() => handleApproveWFH("REJECTED")} variant="contained" color="error" disabled={!description}>
                      Reject
                    </MDButton>
                  </Box>
                </Card>
              ) : null}
            </>
          ) : null}
        </Box>
      </Drawer>
    </DashboardLayout>
  );
}

export default ApproveWFH;
